import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import './FreelancerProfile.css';

function defaultAvatarFor(nameOrId) {
  const initial = String(nameOrId || 'U').trim().slice(0, 1).toUpperCase() || 'U';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="48" fill="#108a5f"/><text x="48" y="58" text-anchor="middle" font-family="Arial, sans-serif" font-size="38" font-weight="700" fill="#ffffff">${initial}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function normalizeFreelancer(item = {}) {
  const name = String(item.name || item.username || '服务者').trim();
  return {
    ...item,
    id: item.id,
    name,
    avatar: item.avatar || defaultAvatarFor(name || item.id),
    title: item.title || '平台服务者',
    bio: item.bio || '',
    skills: Array.isArray(item.skills) ? item.skills.map(String).filter(Boolean) : [],
    services: Array.isArray(item.services) ? item.services : [],
    reviewList: Array.isArray(item.reviewList) ? item.reviewList : [],
    verified: Boolean(item.verified),
    rating: Number(item.rating || 0),
    reviews: Number(item.reviews || 0),
    rate: Number(item.rate || 0),
    jobs: Number(item.jobs || 0),
    success: Number(item.success || 0),
    responseTime: item.responseTime || '平台内沟通',
    created_at: item.created_at || null,
    country: item.country || '',
    city: item.city || '',
    district: item.district || '',
    ratingDistribution: item.ratingDistribution || {},
  };
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString();
}

const FP_TEXT = {
  zh: {
    loading: '加载中...',
    notFound: '找不到该技术员',
    back: '返回列表',
    verified: '已认证',
    reviews: '评价',
    perHour: '小时',
    message: '私信',
    hire: '立即雇佣',
    about: '关于',
    portfolio: '服务',
    skills: '技能',
    completed: '完成服务',
    success: '成功率',
    response: '响应时间',
    avgRating: '平均评分',
    joined: '加入时间',
    location: '位置',
    ratingBreakdown: '评分分布',
    noBio: '该技术员暂未填写个人介绍。',
    noServices: '该技术员暂无已发布服务。',
    noReviews: '暂无真实评价。',
    noSkills: '暂无技能标签。',
    viewService: '查看服务',
  },
  en: {
    loading: 'Loading...',
    notFound: 'Provider not found',
    back: 'Back to list',
    verified: 'Verified',
    reviews: 'reviews',
    perHour: 'hr',
    message: 'Message',
    hire: 'Hire now',
    about: 'About',
    portfolio: 'Services',
    skills: 'Skills',
    completed: 'Jobs completed',
    success: 'Success rate',
    response: 'Response time',
    avgRating: 'Average rating',
    joined: 'Joined',
    location: 'Location',
    ratingBreakdown: 'Rating breakdown',
    noBio: 'This provider has not added a bio yet.',
    noServices: 'This provider has no published services yet.',
    noReviews: 'No verified reviews yet.',
    noSkills: 'No skill tags yet.',
    viewService: 'View service',
  },
};

export default function FreelancerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const text = FP_TEXT[language] || FP_TEXT.en;
  const [activeTab, setActiveTab] = useState('portfolio');
  const [freelancer, setFreelancer] = useState(null);
  const [detailLoaded, setDetailLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetailLoaded(false);
    setFreelancer(null);
    api.get(`/marketplace/freelancers/${id}`)
      .then(res => {
        if (!cancelled) setFreelancer(normalizeFreelancer(res.data));
      })
      .catch(() => {
        if (!cancelled) setFreelancer(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoaded(true);
      });
    return () => { cancelled = true; };
  }, [id]);

  const serviceList = useMemo(() => (
    Array.isArray(freelancer?.services) ? freelancer.services.filter(service => service?.id) : []
  ), [freelancer]);
  const reviewList = useMemo(() => (
    Array.isArray(freelancer?.reviewList) ? freelancer.reviewList : []
  ), [freelancer]);
  const primaryService = serviceList[0];
  const ratingDistribution = freelancer?.ratingDistribution || {};
  const profileLocation = [freelancer?.country, freelancer?.city, freelancer?.district].filter(Boolean).join(' · ');

  if (!freelancer && !detailLoaded) return <div className="loading">{text.loading}</div>;

  if (!freelancer) {
    return (
      <div className="fp-not-found">
        <h2>{text.notFound}</h2>
        <Link to="/freelancers" className="btn btn-primary">{text.back}</Link>
      </div>
    );
  }

  const startMessage = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    const params = new URLSearchParams({
      peerName: freelancer.name,
      peerSeed: freelancer.name,
      peerId: String(freelancer.id),
    });
    navigate(`/messages?${params.toString()}`);
  };

  const startHire = () => {
    if (primaryService?.id) {
      navigate(`/services/listing-${primaryService.id}`);
      return;
    }
    navigate('/tasks/create');
  };

  return (
    <div className="fp-page">
      <div className="fp-hero">
        <div className="container fp-hero-inner">
          <img src={freelancer.avatar} alt={freelancer.name} className="fp-avatar" />
          <div className="fp-info">
            <h1>{freelancer.name}</h1>
            <p className="fp-title">{freelancer.title}</p>
            <div className="fp-badges">
              {freelancer.verified && <span className="fp-badge verified">✓ {text.verified}</span>}
              <span className="fp-badge pro">{freelancer.responseTime}</span>
            </div>
            <div className="fp-rating-row">
              <span>★ {freelancer.rating} ({freelancer.reviews} {text.reviews})</span>
              <span className="fp-rate">¥{freelancer.rate}/{text.perHour}</span>
            </div>
            {(profileLocation || freelancer.created_at) && (
              <div className="fp-profile-meta">
                {profileLocation && <span>{text.location}: {profileLocation}</span>}
                {freelancer.created_at && <span>{text.joined}: {formatDate(freelancer.created_at)}</span>}
              </div>
            )}
          </div>
          <div className="fp-actions">
            <button className="btn btn-ghost" onClick={startMessage}>{text.message}</button>
            <button className="btn btn-primary" onClick={startHire}>{text.hire}</button>
          </div>
        </div>
      </div>

      <div className="container fp-body">
        <div className="fp-main">
          <section className="fp-card">
            <h2>{text.about}</h2>
            <p>{freelancer.bio || text.noBio}</p>
          </section>

          <section className="fp-card">
            <div className="fp-tabs">
              <button className={activeTab === 'portfolio' ? 'active' : ''} onClick={() => setActiveTab('portfolio')}>{text.portfolio}</button>
              <button className={activeTab === 'reviews' ? 'active' : ''} onClick={() => setActiveTab('reviews')}>{text.reviews} ({reviewList.length})</button>
              <button className={activeTab === 'skills' ? 'active' : ''} onClick={() => setActiveTab('skills')}>{text.skills}</button>
            </div>

            {activeTab === 'portfolio' && (
              serviceList.length > 0 ? (
                <div className="fp-portfolio">
                  {serviceList.map(service => (
                    <Link key={service.id} to={`/services/listing-${service.id}`} className="fp-pf-item">
                      <div className="fp-pf-title">{service.title}</div>
                      <p>{service.description}</p>
                      <div className="fp-pf-tags">
                        {[service.category, service.method, service.price].filter(Boolean).map(tag => (
                          <span key={tag} className="fp-tag">{tag}</span>
                        ))}
                      </div>
                      <strong className="fp-view-service">{text.viewService}</strong>
                    </Link>
                  ))}
                </div>
              ) : <div className="fp-empty-panel">{text.noServices}</div>
            )}

            {activeTab === 'reviews' && (
              reviewList.length > 0 ? (
                <div className="fp-reviews">
                  {reviewList.map(review => (
                    <div key={review.id} className="fp-review">
                      <div className="fp-review-head">
                        <strong>{review.reviewer}</strong>
                        <span className="fp-stars">
                          {[...Array(5)].map((_, index) => <span key={index} className={index < Number(review.rating || 0) ? 'on' : ''}>★</span>)}
                        </span>
                        <span className="fp-review-date">{formatDate(review.date)}</span>
                      </div>
                      {review.task && <div className="fp-review-task">{review.task}</div>}
                      <p>{review.comment}</p>
                    </div>
                  ))}
                </div>
              ) : <div className="fp-empty-panel">{text.noReviews}</div>
            )}

            {activeTab === 'skills' && (
              freelancer.skills.length > 0 ? (
                <div className="fp-skills-list">
                  {freelancer.skills.map(skill => <span key={skill} className="fp-skill">{skill}</span>)}
                </div>
              ) : <div className="fp-empty-panel">{text.noSkills}</div>
            )}
          </section>
        </div>

        <aside className="fp-side">
          <div className="fp-card">
            <div className="fp-stat"><span>{text.completed}</span><strong>{freelancer.jobs}</strong></div>
            <div className="fp-stat"><span>{text.success}</span><strong>{freelancer.success}%</strong></div>
            <div className="fp-stat"><span>{text.response}</span><strong>{freelancer.responseTime}</strong></div>
            <div className="fp-stat"><span>{text.avgRating}</span><strong>★ {freelancer.rating}</strong></div>
          </div>
          <div className="fp-card fp-rating-breakdown">
            <h2>{text.ratingBreakdown}</h2>
            {[5, 4, 3, 2, 1].map(star => {
              const count = Number(ratingDistribution[star] || 0);
              const width = freelancer.reviews ? `${Math.round((count / freelancer.reviews) * 100)}%` : '0%';
              return (
                <div className="fp-breakdown-row" key={star}>
                  <span>{star} ★</span>
                  <i><b style={{ width }} /></i>
                  <em>{count}</em>
                </div>
              );
            })}
          </div>
          <div className="fp-card fp-rate-card">
            <div className="fp-big-rate">¥{freelancer.rate}<span>/{text.perHour}</span></div>
            <button className="btn btn-primary btn-block" onClick={startHire}>{text.hire}</button>
          </div>
        </aside>
      </div>
    </div>
  );
}
